import React, { useCallback, useState } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassesContainer from './ClassesContainer';
import ClassesDateRange from './ClassesDateRange';
import DraftChip from './ClassPage/DraftChip';
import Instructors from './Instructors';
import { type ClassWithInstructors } from './types';
import { ClassStatus } from '../../../../context/types';
import { getUrls } from '../../../../utils/url';

const ClassLink = styled(Link)`
    color: ${(props) => props.theme.colors.primary.main};
`;

const MenuLink = styled(Link)`
    color: inherit;
`;

const StyledIconButton = styled(IconButton)`
    svg {
        width: 32px;
        height: 32px;
    }
`;

const Cell = styled(TableCell)`
    padding: 0 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 350px;
`;

export interface ClassTableRowProps {
    record: ClassWithInstructors;
}

const ClassTableRow: React.FC<ClassTableRowProps> = React.memo(({ record }) => {
    const { path } = useRouteMatch();
    const { setSelectedClass, setSelectedDownloadTranscriptsClass } = useContainer(ClassesContainer);

    const [anchorEl, setAnchorEl] = useState(null);

    const { t } = useTranslation('admin_settings');

    const renderClassName = useCallback((classRecord) => {
        const isDraft = classRecord.status === ClassStatus.Draft;

        return (
            <>
                {classRecord.class_name} {isDraft && <DraftChip />}
            </>
        );
    }, []);

    const name = new URLSearchParams({ className: record.class_name });
    const classLink = `${path}/${record.class_uuid}?${name.toString()}`;

    const onCopyURL = () => {
        const { vanityUrl } = getUrls();

        const link = `${vanityUrl}/class/${record.class_uuid}`;

        navigator.clipboard.writeText(link);
    };

    const onActionsClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadChatClick = () => {
        setSelectedClass(record);
        handleClose();
    };
    const handleDownloadTranscriptsClick = () => {
        setSelectedDownloadTranscriptsClass(record);
        handleClose();
    };

    const handleCopyUrlClick = () => {
        onCopyURL();
        handleClose();
    };

    return (
        <TableRow key={record.class_uuid}>
            <Cell>
                <ClassLink to={classLink}>{renderClassName(record)}</ClassLink>
            </Cell>

            <Cell>
                <Instructors instructors={record.instructors} />
            </Cell>
            <Cell>
                <ClassesDateRange start={record.starting_at} end={record.ending_at} />
            </Cell>
            <Cell>
                <StyledIconButton size="small" onClick={onActionsClick}>
                    <MoreHorizIcon />
                </StyledIconButton>
            </Cell>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem onClick={handleClose}>
                    <MenuLink to={classLink}>{t('classes.edit-class')}</MenuLink>
                </MenuItem>
                {record.status !== ClassStatus.Draft && (
                    <MenuItem onClick={handleDownloadChatClick}>{t('classes.download-chat')}</MenuItem>
                )}
                {record.status !== ClassStatus.Draft && (
                    <MenuItem onClick={handleDownloadTranscriptsClick}>{t('classes.download-transcript')}</MenuItem>
                )}
                {record.status !== ClassStatus.Draft && (
                    <MenuItem onClick={handleCopyUrlClick}>{t('classes.copy-url')}</MenuItem>
                )}
            </Menu>
        </TableRow>
    );
});

export default ClassTableRow;
